import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDateTime, formatTime } from "@/lib/i18n";
import {
  GroupSessionForm,
  type GroupSessionFormValues,
} from "@/components/pages/bookings/group-session-form";

interface GroupSessionSummary {
  id: string;
  serviceName: string;
  instructorName: string;
  room: string | null;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  enrolledCount: number;
  waitlistCount: number;
  isRecurring: boolean;
  status: "active" | "cancelled" | "completed";
}

interface GroupSessionDetail {
  id: string;
  serviceId: string;
  serviceName: string;
  personId: string;
  instructorName: string;
  startAt: string;
  endAt: string;
  assignedResourceIds: string[];
  capacityOverride: number | null;
  maxParticipants: number | null;
  enrolledCount: number;
  waitlistCount: number;
  availableSpots: number | null;
  waitlistConfig: { mode: "auto_add" | "first_to_claim"; claimTimeoutMinutes?: number } | null;
  locationAddress: string | null;
  customerNotes: string | null;
  recurrence: GroupSessionFormValues["recurrence"];
  status: "active" | "cancelled" | "completed";
  cancelledAt: string | null;
  cancellationReason: string | null;
}

interface GroupParticipant {
  id: string;
  bookingId: string;
  personId: string;
  personName: string;
  status: "enrolled" | "checked_in" | "attended" | "no_show" | "cancelled" | "waitlisted";
  checkedInAt: string | null;
  creditDeducted: number | null;
}

interface ContactSummary {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
}

interface GroupServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  maxParticipants: number | null;
  waitlistConfig: { mode: "auto_add" | "first_to_claim"; claimTimeoutMinutes?: number } | null;
}

interface CommunitySpaceOption {
  id: string;
  name: string;
  isMember: boolean;
}

interface GroupSessionContactOption {
  id: string;
  label: string;
  email: string | null;
  phone: string | null;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function toContactOption(contact: ContactSummary): GroupSessionContactOption {
  return {
    id: contact.id,
    label: [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || contact.email || contact.phone || "Unnamed contact",
    email: contact.email,
    phone: contact.phone,
  };
}

export default function GroupSessionsPage() {
  const [sessions, setSessions] = useState<GroupSessionSummary[]>([]);
  const [services, setServices] = useState<GroupServiceOption[]>([]);
  const [communitySpaces, setCommunitySpaces] = useState<CommunitySpaceOption[]>([]);
  const [contacts, setContacts] = useState<GroupSessionContactOption[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<GroupSessionDetail | null>(null);
  const [participants, setParticipants] = useState<GroupParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<GroupSessionDetail | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [contactFilter, setContactFilter] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");

  const enrolledParticipants = useMemo(
    () => participants.filter((participant) => ["enrolled", "checked_in", "attended"].includes(participant.status)),
    [participants],
  );
  const waitlistedParticipants = useMemo(
    () => participants.filter((participant) => participant.status === "waitlisted"),
    [participants],
  );
  const visibleContacts = useMemo(() => {
    const query = contactFilter.trim().toLowerCase();
    if (!query) return contacts.slice(0, 50);
    return contacts.filter((contact) => (
      contact.label.toLowerCase().includes(query)
      || contact.email?.toLowerCase().includes(query)
      || contact.phone?.toLowerCase().includes(query)
    )).slice(0, 50);
  }, [contactFilter, contacts]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sessionsResponse, servicesResponse, communitySpacesResponse, contactsResponse] = await Promise.all([
        apiFetch<{ sessions: GroupSessionSummary[] }>("/crm/bookings/groups"),
        apiFetch<{ services: GroupServiceOption[] }>("/crm/bookings/groups/services"),
        apiFetch<{ data: CommunitySpaceOption[] }>("/crm/communities/spaces"),
        apiFetch<{ data: { items: ContactSummary[] } }>("/crm/people?limit=100"),
      ]);
      setSessions(sessionsResponse.sessions);
      setServices(servicesResponse.services);
      setCommunitySpaces((communitySpacesResponse.data ?? []).filter((space) => space.isMember));
      setContacts((contactsResponse.data.items ?? []).map(toContactOption));
      setSelectedSessionId((current) => current ?? sessionsResponse.sessions[0]?.id ?? null);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError, "Could not load group sessions."));
      setSessions([]);
      setServices([]);
      setCommunitySpaces([]);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSelectedSession = useCallback(async (sessionId: string) => {
    setDetailLoading(true);
    setActionError(null);
    try {
      const [sessionResponse, participantsResponse] = await Promise.all([
        apiFetch<{ session: GroupSessionDetail }>(`/crm/bookings/groups/${sessionId}`),
        apiFetch<{ participants: GroupParticipant[] }>(`/crm/bookings/groups/${sessionId}/participants`),
      ]);
      setSelectedSession(sessionResponse.session);
      setParticipants(participantsResponse.participants);
    } catch (caughtError) {
      setActionError(getErrorMessage(caughtError, "Could not load group session detail."));
      setSelectedSession(null);
      setParticipants([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!selectedSessionId) return;
    void loadSelectedSession(selectedSessionId);
  }, [loadSelectedSession, selectedSessionId]);

  async function refreshSession(sessionId: string) {
    const [sessionsResponse, detailResponse, participantsResponse] = await Promise.all([
      apiFetch<{ sessions: GroupSessionSummary[] }>("/crm/bookings/groups"),
      apiFetch<{ session: GroupSessionDetail }>(`/crm/bookings/groups/${sessionId}`),
      apiFetch<{ participants: GroupParticipant[] }>(`/crm/bookings/groups/${sessionId}/participants`),
    ]);
    setSessions(sessionsResponse.sessions);
    setSelectedSession(detailResponse.session);
    setParticipants(participantsResponse.participants);
  }

  async function handleSave(payload: GroupSessionFormValues) {
    setSaving(true);
    setActionError(null);
    try {
      if (editing) {
        await apiFetch<{ session: GroupSessionDetail }>(`/crm/bookings/groups/${editing.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        await refreshSession(editing.id);
      } else {
        const response = await apiFetch<{ session: GroupSessionDetail }>("/crm/bookings/groups", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        await loadPage();
        setSelectedSessionId(response.session.id);
      }
      setEditing(null);
      setIsFormOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleParticipantAction(path: string, key: string, body?: Record<string, unknown>) {
    if (!selectedSessionId) return;
    setBusyKey(key);
    setActionError(null);
    try {
      await apiFetch(path, body ? { method: "POST", body: JSON.stringify(body) } : { method: "POST" });
      setSelectedContactId("");
      setContactFilter("");
      await refreshSession(selectedSessionId);
    } catch (caughtError) {
      setActionError(getErrorMessage(caughtError, "Could not update group session."));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCancelSession() {
    if (!selectedSessionId) return;
    await handleParticipantAction(`/crm/bookings/groups/${selectedSessionId}/cancel`, "cancel-session", {
      reason: "Cancelled from group session manager",
    });
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted group session management</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Group sessions</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)]">
              This operator surface is wired to the live CRM booking group routes for create, edit, enroll, waitlist, and check-in flows.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setIsFormOpen(true);
            }}
            className="inline-flex h-11 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Create group session
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}
      {actionError ? (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {actionError}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-sm text-[var(--color-muted-foreground)]">
          Loading live group sessions...
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.1fr_1.9fr]">
          <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Scheduled sessions</h2>
              <span className="rounded-full bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
                {sessions.length} total
              </span>
            </div>
            <div className="mt-4 space-y-3">
              {sessions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)]">
                  No group sessions yet.
                </div>
              ) : sessions.map((session) => {
                const isSelected = session.id === selectedSessionId;
                const isFull = session.enrolledCount >= session.maxParticipants;
                return (
                  <button
                    key={session.id}
                    type="button"
                    onClick={() => setSelectedSessionId(session.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-colors ${isSelected ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)] hover:bg-[var(--color-surface)]"}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium text-[var(--color-foreground)]">{session.serviceName}</div>
                        <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{session.dayOfWeek} · {session.startTime}-{session.endTime}</div>
                        <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{session.instructorName}{session.room ? ` · ${session.room}` : ""}</div>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${session.status === "cancelled" ? "bg-[var(--color-error)]/10 text-[var(--color-error)]" : isFull ? "bg-[var(--color-warning)]/12 text-[var(--color-warning)]" : "bg-[var(--color-success)]/10 text-[var(--color-success)]"}`}>
                        {session.status === "cancelled" ? "Cancelled" : isFull ? "Full" : "Open"}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                      <span>{session.enrolledCount}/{session.maxParticipants} enrolled</span>
                      <span>{session.waitlistCount} waitlisted</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            {!selectedSessionId ? (
              <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)]">
                Select a session to manage enrollment and attendance.
              </div>
            ) : detailLoading || !selectedSession ? (
              <div className="rounded-2xl border border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)]">
                Loading session detail...
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-[var(--color-foreground)]">{selectedSession.serviceName}</h2>
                    <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                      {formatDateTime(selectedSession.startAt)} to {formatTime(selectedSession.endAt)}
                    </p>
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Instructor: {selectedSession.instructorName}</p>
                    {selectedSession.locationAddress ? (
                      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Location: {selectedSession.locationAddress}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(selectedSession);
                        setIsFormOpen(true);
                      }}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                    >
                      Edit session
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleCancelSession()}
                      disabled={busyKey === "cancel-session" || selectedSession.status === "cancelled"}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-[var(--color-error)]/20 px-4 text-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error)]/8 disabled:opacity-60"
                    >
                      {busyKey === "cancel-session" ? "Cancelling..." : "Cancel session"}
                    </button>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                    <div className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Enrolled</div>
                    <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{enrolledParticipants.length}</div>
                  </div>
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                    <div className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Empty seats</div>
                    <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{selectedSession.availableSpots ?? 0}</div>
                  </div>
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                    <div className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Waitlist</div>
                    <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{waitlistedParticipants.length}</div>
                  </div>
                </div>

                <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
                  <h3 className="text-lg font-semibold text-[var(--color-foreground)]">Enroll or waitlist attendee</h3>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    Full sessions can keep accepting waitlist entries through the mounted waitlist route.
                  </p>
                  <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
                    <input
                      value={contactFilter}
                      onChange={(event) => setContactFilter(event.target.value)}
                      placeholder="Filter contacts"
                      className="h-11 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    />
                    <select
                      value={selectedContactId}
                      onChange={(event) => setSelectedContactId(event.target.value)}
                      className="h-11 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    >
                      <option value="">Select contact</option>
                      {visibleContacts.map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.label}{contact.email ? ` · ${contact.email}` : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void handleParticipantAction(`/crm/bookings/groups/${selectedSession.id}/participants`, `enroll-${selectedContactId}`, { personId: selectedContactId })}
                      disabled={!selectedContactId || busyKey !== null || selectedSession.status === "cancelled"}
                      className="inline-flex h-11 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    >
                      {busyKey?.startsWith("enroll-") ? "Enrolling..." : "Enroll"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleParticipantAction(`/crm/bookings/groups/${selectedSession.id}/waitlist`, `waitlist-${selectedContactId}`, { personId: selectedContactId })}
                      disabled={!selectedContactId || busyKey !== null || selectedSession.status === "cancelled"}
                      className="inline-flex h-11 items-center justify-center rounded-2xl border border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-background)] disabled:opacity-60"
                    >
                      {busyKey?.startsWith("waitlist-") ? "Adding..." : "Waitlist"}
                    </button>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-3xl border border-[var(--color-border)] p-5">
                    <h3 className="text-lg font-semibold text-[var(--color-foreground)]">Enrolled attendees</h3>
                    <div className="mt-4 space-y-3">
                      {enrolledParticipants.length === 0 ? (
                        <p className="text-sm text-[var(--color-muted-foreground)]">No enrolled attendees yet.</p>
                      ) : enrolledParticipants.map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
                          <div>
                            <div className="font-medium text-[var(--color-foreground)]">{participant.personName}</div>
                            <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">Status: {participant.status.replace("_", " ")}</div>
                          </div>
                          {participant.status === "enrolled" ? (
                            <button
                              type="button"
                              onClick={() => void handleParticipantAction(`/crm/bookings/groups/${selectedSession.id}/participants/${participant.personId}/check-in`, `check-in-${participant.personId}`)}
                              disabled={busyKey === `check-in-${participant.personId}`}
                              className="inline-flex h-9 items-center justify-center rounded-2xl border border-[var(--color-border)] px-3 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-background)] disabled:opacity-60"
                            >
                              {busyKey === `check-in-${participant.personId}` ? "Checking in..." : "Check in"}
                            </button>
                          ) : (
                            <span className="rounded-full bg-[var(--color-success)]/10 px-2.5 py-1 text-xs font-semibold text-[var(--color-success)]">
                              {participant.status === "checked_in" ? "Checked in" : "Complete"}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-[var(--color-border)] p-5">
                    <h3 className="text-lg font-semibold text-[var(--color-foreground)]">Waitlist</h3>
                    <div className="mt-4 space-y-3">
                      {waitlistedParticipants.length === 0 ? (
                        <p className="text-sm text-[var(--color-muted-foreground)]">Nobody is waiting yet.</p>
                      ) : waitlistedParticipants.map((participant, index) => (
                        <div key={participant.id} className="flex items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
                          <div>
                            <div className="font-medium text-[var(--color-foreground)]">{participant.personName}</div>
                            <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">Position {index + 1}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleParticipantAction(`/crm/bookings/groups/${selectedSession.id}/waitlist/${participant.personId}/claim`, `claim-${participant.personId}`)}
                            disabled={(selectedSession.availableSpots ?? 0) < 1 || busyKey === `claim-${participant.personId}`}
                            className="inline-flex h-9 items-center justify-center rounded-2xl border border-[var(--color-border)] px-3 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-background)] disabled:opacity-60"
                          >
                            {busyKey === `claim-${participant.personId}` ? "Promoting..." : (selectedSession.availableSpots ?? 0) > 0 ? "Promote" : "Full"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
                  Primary contact: <span className="font-medium text-[var(--color-foreground)]">{selectedSession.personId}</span>
                  {selectedSession.customerNotes ? (
                    <>
                      {" · "}Notes: <span className="font-medium text-[var(--color-foreground)]">{selectedSession.customerNotes}</span>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      <GroupSessionForm
        isOpen={isFormOpen}
        mode={editing ? "edit" : "create"}
        onClose={() => {
          setIsFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
        isSubmitting={saving}
        services={services}
        communitySpaces={communitySpaces}
        initialValues={editing ? {
          serviceId: editing.serviceId,
          personId: editing.personId,
          startAt: editing.startAt,
          endAt: editing.endAt,
          capacityOverride: editing.capacityOverride,
          locationAddress: editing.locationAddress ?? "",
          customerNotes: editing.customerNotes ?? "",
          communitySpaceId: null,
          recurrence: editing.recurrence,
        } : {
          serviceId: "",
          personId: "",
          startAt: new Date().toISOString(),
          endAt: new Date(Date.now() + 60 * 60_000).toISOString(),
          capacityOverride: null,
          locationAddress: "",
          customerNotes: "",
          communitySpaceId: null,
          recurrence: null,
        }}
      />
    </div>
  );
}
